/**
 * The identity check, reduced to the one question the app asks (MYP-142): may this customer create
 * orders? There is no "under review" state: the vendor has not said which `status` value means it,
 * and inventing one would be guessing — the not-verified screen's text covers it instead.
 */
export type IdentityVerification = 'verified' | 'notVerified';

/**
 * The read-only account profile MYP-72's Account screen shows.
 *
 * An allow-list, not the shape of any endpoint. `alipay_page2` is what will eventually fill this
 * (see MYP-73's Step 0 — its `envelope: 'codeOnly'` classification currently discards every field
 * but the code, which has to be fixed before that wiring can happen), and once it does this type
 * stays exactly the fields this screen is allowed to show. A strip-list of "everything except the
 * named-sensitive ones" would let a future field the vendor adds appear here by default; an
 * allow-list means a new field is invisible until someone deliberately adds it.
 */
export interface AccountProfile {
  readonly customerId: string;
  /**
   * `firstname` and `lastname` from the record, joined. Absent when the record has neither — a
   * blank is reported as absent rather than shown as an empty name.
   */
  readonly name?: string;
  readonly vipTier: string;
  /**
   * Not named in the MYP-72 ticket text — the mentor mockup shows an "ACTIVE" pill. Carried
   * through as given rather than invented from nothing, but unconfirmed until MYP-73's live check
   * says what field (if any) actually holds it.
   */
  readonly status: 'ACTIVE' | 'INACTIVE';
  /**
   * Whether the customer's identity (IC/passport + face photo) has been verified by MyPoz staff
   * (MYP-142). The vendor only creates orders for verified customers, so Create order checks this
   * first. See `identityVerification` in account-wire.ts for how it is read.
   */
  readonly verification: IdentityVerification;
  /** `telephone`, or `phone` when that is blank. Absent when the record has neither. */
  readonly phone?: string;
  readonly email?: string;
  /**
   * The identity-document number (`idno`), exactly as held. Never rendered directly — always
   * through `maskNric`. Not necessarily a Malaysian NRIC: the record also carries an `idtype`.
   */
  readonly nric?: string;
  /** Absent for an account with no linked Taobao account. */
  readonly taobaoAccount?: string;
  readonly walletCurrency: string;
  /**
   * The wallet balance, in cents, from `alipay_page2`'s `balance` (MYP-105).
   *
   * Absent when the record carries no readable, non-negative amount — which is not the same as
   * zero, and the caller decides what to show for it. Kept on the profile rather than a second
   * fetch because it arrives in the same response, and the session reads it once.
   */
  readonly walletBalanceCents?: number;
  readonly paymentMode: 'MANUAL' | 'AUTO';
  /** `claimpkgtype` — see the CLAIM_TYPE dictionary in apps/web/src/features/account. */
  readonly claimType: string;
  readonly autoInbound: boolean;
  /**
   * A customer-level preference, not a flag on the warehouse itself. `warehouses` has no such
   * field — see the comment on Dashboard's `ShipToAddressCard` — so this is account data, kept
   * separate from the warehouse list it points into. Absent for an account with no preference set.
   */
  readonly defaultWarehouseId?: string;
}

/**
 * Masks an identity-document number for display, keeping just enough for the customer to recognise
 * their own.
 *
 * - A Malaysian NRIC (`YYMMDD-PB-####`, with or without the dashes) becomes `YYMMDD-PB-••##`: the
 *   last two digits stay visible.
 * - Anything else is masked all but its last four characters, and one of four or fewer is masked
 *   whole. The record's `idno` is whatever the customer registered — an NRIC, a passport, a
 *   mainland ID card — and there is no way to know which shape to expect. An earlier version showed
 *   an unrecognised format whole, on the reasoning that a partial redaction of a foreign format
 *   implies a promise it cannot keep; but a number of unknown shape printed in full is the worse
 *   failure, so an unrecognised one is now treated as sensitive.
 */
export function maskNric(nric: string): string {
  const match = /^(\d{6})-?(\d{2})-?\d{2}(\d{2})$/.exec(nric);
  if (match) return `${match[1] ?? ''}-${match[2] ?? ''}-••${match[3] ?? ''}`;

  if (nric.length <= 4) return '•'.repeat(nric.length);
  return `${'•'.repeat(nric.length - 4)}${nric.slice(-4)}`;
}
